import { t, useLocale } from "@/i18n/locale";
import { useEffect, useSyncExternalStore } from "react";

import {
  checkApiCompatibility,
  getPwaCompatibilityState,
  requestPwaUpdate,
  subscribePwaCompatibility,
} from "../app/clientContract";

/**
 * Blocking compatibility gate for an out-of-date PWA.
 *
 * Mounted at the very root (alongside `App`) so it covers EVERY shell,
 * including the standalone sign-in/callback routes: an outdated build must not
 * be usable just because it happens to be on a route that doesn't fetch.
 *
 * The 426 itself never reaches a screen — `clientContract.ts` observes it and
 * flips this gate, so individual API errors are never shown to the user.
 */
export default function PwaUpdateGate() {
  useLocale();
  const state = useSyncExternalStore(
    subscribePwaCompatibility,
    getPwaCompatibilityState,
    getPwaCompatibilityState,
  );

  useEffect(() => {
    void checkApiCompatibility();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void checkApiCompatibility();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  if (state.phase === "compatible") return null;

  const failed = state.phase === "update_failed";

  return (
    <div className="consent-modal-overlay" role="presentation">
      <div
        className="consent-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="pwa-update-title"
      >
        <h3 id="pwa-update-title" className="consent-modal-title">
          {failed ? t("Update failed") : t("Ato has been updated")}
        </h3>
        <p className="consent-modal-note">
          {failed
            ? t("We couldn't update this version. Reload the page to try again.")
            : t("This version of ato.run is out of date. Update to continue.")}
        </p>
        <div className="consent-modal-actions">
          <button
            type="button"
            className="consent-modal-approve"
            onClick={() => {
              if (failed) {
                window.location.reload();
                return;
              }
              void requestPwaUpdate();
            }}
          >
            {failed ? t("Reload") : t("Update now")}
          </button>
        </div>
      </div>
    </div>
  );
}
